import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  DateFilterModule,
  NumberFilterModule,
  PaginationModule,
  QuickFilterModule,
  TextFilterModule,
  ValidationModule,
  ExternalFilterModule,
  ModuleRegistry,
  themeAlpine,
  CustomFilterModule,
  RowAutoHeightModule
} from 'ag-grid-community';
import { ClientSideRowModelModule } from 'ag-grid-community'; 
import { ProgramOffice } from '../../services/Types';
import { programOffice } from '../../services/EventService';
import { IconButton, Icon, Typography, Badge } from '@mui/material';
import Office from '../../images/office.svg';
import { useProgramOfficeData } from '../../hooks/useProgramOfficeData';

ModuleRegistry.registerModules([
  RowAutoHeightModule,
  PaginationModule,
  QuickFilterModule,
  ClientSideRowModelModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  ExternalFilterModule,
  CustomFilterModule,
  ValidationModule /* Development Only */,
]);

interface ProgramOfficeGridProps {
  searchText: string;
  setAccountSelected: (accountSelected: ProgramOffice) => void;
}

export const ProgramOfficeGrid: React.FC<ProgramOfficeGridProps> = ({ searchText, setAccountSelected }) => {
  const gridRef = useRef<AgGridReact>(null);
  const containerStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);
  const gridStyle = useMemo(() => ({ height: "calc(100vh - 225px)", width: "100%" }), []);
  const { programOfficeCache } = useProgramOfficeData();

  // const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);

  // set the quick filter text to the external search text. Re-run when programOfficeCache loads so the filter
  // is applied on initial page load with ?q= in URL (grid api may not be ready until data arrives).
  const prevDataLengthRef = useRef(0);
  useEffect(() => {
    const applyQuickFilter = () => {
      const api = gridRef.current?.api;
      if (!api) return;
      api.setGridOption("quickFilterText", searchText);
    };
    const hadData = prevDataLengthRef.current > 0;
    const hasData = programOfficeCache.length > 0;
    prevDataLengthRef.current = programOfficeCache.length;
    if (hasData && !hadData) {
      const t = setTimeout(applyQuickFilter, 100);
      return () => clearTimeout(t);
    }
    applyQuickFilter();
  }, [searchText, programOfficeCache.length]);

  // Set up the column definitions for the grid
  const defaultColDef = useMemo(() => ({
    filter: true,
    minWidth: 50,
    resizable: false,
    hide: true,
    autoHeight: true,
    wrapText: true,
  }), []);

  const columnDefs: ColDef<ProgramOffice>[] = [
    { field: 'id' as keyof ProgramOffice,
      headerName: 'ID',
    },
    { field: 'title',
      headerName: 'Offices',
      hide: false,
      sort: 'asc',
      valueGetter: (params: any) => {
        return params.data.title;
      },
      cellRenderer: (params: any) => {
        // return a badge with the title and acronym
        return <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginTop: '10px', paddingBottom: '10px' }}>
          <Badge color="secondary" variant="standard">
            <img src={Office} alt="Office" style={{ width: '36px', height: '36px' }} />
          </Badge>
          <Typography variant="body2" style={{ lineHeight: '1em' }}>{params.data.title}</Typography>
        </div>;
      },
      getQuickFilterText: (params: any) => {
        return `
          ${params.data.title}
          ${params.data.acronym}
        `;
      }
    },
    { field: 'acronym',
      headerName: 'Acronym',
    },
    { field: 'levelOneApprover',
      headerName: 'Program Office',
      valueGetter: (params: any) => {
        return params.data?.levelOneApprover ? params.data?.levelOneApprover?.name + ' (' + params.data?.levelOneApprover?.email + ')' : '';
      },
      getQuickFilterText: (params: any) => {
        return params.data?.levelOneApprover ? params.data?.levelOneApprover?.name : '';
      }
    }
  ];


  return (
    <div className="ag-theme-alpine" style={gridStyle}>
      <AgGridReact
        ref={gridRef}
        theme={themeAlpine}
        rowData={programOfficeCache}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        onRowClicked={(programOffice) => setAccountSelected(programOffice.data)}
      />
    </div>
  );
}; 